import { WorkforceTeamEditorState } from '../../../services/payroll-workforce/workforce-team-editor.state';
import { WorkforceTeamCreationState } from '../../../services/payroll-workforce/workforce-team-creation.state';
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatchingSelection, WorkforceTeamCriteriaState } from '../../../services/payroll-workforce/workforce-team-criteria.state';
import { WorkforceTeamMembersState } from '../../../services/payroll-workforce/workforce-team-members.state';
import { WorkforceBrowseState } from '../../../services/payroll-workforce/workforce-browse.state';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { UsersService } from '../../../services/users/users.service';
import {
  PayrollCensusAttributeSnapshotCriteria,
  PayrollProviderCapabilities,
  PayrollProviderConnectionResponse,
  PayrollService,
} from '../../../services/payroll/payroll.service';
import { StepDialogComponent, StepState } from '../step-dialog/step-dialog.component';
import { PayrollCensusService, PayrollCensusSession } from '../../../services/payroll-census/payroll-census.service';
import { TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import { TeamsService } from '../../../services/teams/teams.service';
import { PayrollAccessService } from '../../../services/payroll-access/payroll-access.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';
import {
  TEAM_MEMBER_REQUIRED_UPLOAD_COLUMNS,
  TEAM_MEMBER_UPLOAD_COLUMNS,
  TEAM_MEMBER_UPLOAD_TEMPLATE_URL,
} from '../../../constants/enrollment-upload-columns';

const WELLNESS_AMOUNT_MAX = 999999.99;

export type EditTeamDialogData = {
  snapshotEditor?: WorkforceTeamEditorState;
  snapshotProgress?: WorkforceTeamCreationState;
  snapshotSubmit?: (teamName: string, amount: number, matching?: MatchingSelection) => Promise<void>;
  snapshotSelectionCount?: number;
  snapshotSelectedEmployees?: Map<string, {employeeKey: string; name: string}>;
  snapshotIsCurrent?: () => boolean;
  snapshotBrowse?: WorkforceBrowseState;
  snapshotMembers?: WorkforceTeamMembersState;
  snapshotCriteria?: WorkforceTeamCriteriaState;
  isEdit?: boolean;
  startAtMembersStep?: boolean;
  skipMemberSelectionStep?: boolean;
  memberFallbackMode?: boolean;
  team: {
    id?: string;
    name: string;
    amount?: number;
    autoAddNewMembers?: boolean;
    metadata?: { autoAddNewMembers?: boolean };
    membersCount?: number;
    createdAt?: string;
  };
  employees: Array<{
    id: string;
    name: string;
    email?: string;
    employeeId?: string;
    division?: string;
    department?: string;
    state?: string;
    location?: string;
    employeeType?: 'Full-time' | 'Part-time' | 'Contractor';
    employmentType?: 'Full-time' | 'Part-time' | 'Contractor';
    payType?: 'Hourly' | 'Salary';
  }>;
  selectedEmployeeIds: any[];
  initialFieldsContain?: Record<string, any>;
  criteriaSnapshot?: PayrollCensusAttributeSnapshotCriteria | null;
  company?: any;
  providerConnection?: PayrollProviderConnectionResponse | null;
  providerDisplayName?: string;
  teams?: any[];
  manualMode?: boolean;
};

export type EditTeamDialogResult =
  | { saved: false }
  | {
  saved: true;
  teamId: string;
  teamName: string;
  wellnessAmount: number;
  selectedEmployeeIds: string[];
  autoAddNewMembers: boolean;
};

type EmployeeFilterMetadata = { label: string; value: string; displayValue: string };

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-edit-team-dialog',
  templateUrl: './edit-team-dialog.component.html',
  styleUrls: ['./edit-team-dialog.component.scss'],
})
export class EditTeamDialogComponent extends StepDialogComponent implements OnInit, OnDestroy {
  census: PayrollCensusSession;
  protected readonly wellnessAmountMax = WELLNESS_AMOUNT_MAX;

  form = this.fb.group({
    teamName: ['', [Validators.required, Validators.maxLength(80)]],
    wellnessAmount: [0, [Validators.required, Validators.min(0), Validators.max(WELLNESS_AMOUNT_MAX)]],
  });

  // Step 2 state
  search = '';
  autoAddNewMembers = false;
  membersConnectionError = '';
  manualMemberEmail = '';
  isAddingManualMember = false;
  isUploadingManualMembers = false;
  isSavingTeam = false;
  snapshotSettingsInitialized = false;
  snapshotMatching = false;
  snapshotError = '';
  private snapshotErrorFence = '';
  private snapshotAffectedKeys: string[] = [];
  private destroyed = false;
  manualMemberUploadResult: any;
  isManualMemberManagementExpanded = false;

  teamsMap: Record<string, any> = {}
  currentCompany!: Record<string, any>;
  isEdit = false;
  isCreateFromSelectionFlow = false;
  isManualMode = false;
  isMemberFallbackMode = false;
  canCreateManualMembers = false;
  private fallbackBaselineMembers: any[] = [];
  public readonly teamMemberUploadColumns = TEAM_MEMBER_UPLOAD_COLUMNS;
  public readonly requiredUploadColumns = TEAM_MEMBER_REQUIRED_UPLOAD_COLUMNS;
  public readonly requiredUploadColumnVerb =
    TEAM_MEMBER_REQUIRED_UPLOAD_COLUMNS.length === 1 ? 'is' : 'are';
  public readonly uploadTemplateUrl = TEAM_MEMBER_UPLOAD_TEMPLATE_URL;

  get dialogTitle(): string {
    return this.data.snapshotMembers ? 'Edit members' : this.isEdit ? 'Edit team' : 'Create new team';
  }

  get dialogSubtitle(): string {
    if (this.data.snapshotMembers) { return this.data.team.name; }
    if (this.isManualMode) {
      return this.isEdit
        ? 'Update your team details and wellness benefit amount.'
        : 'Create a team now and manage members manually after saving.';
    }
    if (this.isMemberFallbackMode) {
      return 'Update your team details and choose members from existing people already added to company teams.';
    }
    if (this.isCreateFromSelectionFlow) {
      return 'Set team details and wellness benefit amount for your selected employees.';
    }
    return this.isEdit
      ? 'Update your team details and wellness benefit amount.'
      : 'Set up your team details and wellness benefit amount.';
  }

  // expose pass-through properties from census service
  get availableEmployees(): any[] { return this.census.availableEmployees; }
  get availableFilters(): any[] { return this.census.availableFilters; }
  get activeFilters(): any[] { return this.census.activeFilters; }
  get activeFiltersMap(): Record<string, any> { return this.census.activeFiltersMap; }
  get selected(): Set<string> { return this.census.selected; }
  get selectedEmails(): Record<string, any> { return this.census.selectedEmails; }
  get isLoadingInitialData(): boolean { return this.census.isLoadingInitialData; }
  get isEmployeeResultsLoading(): boolean {
    return !!this.census.isLoadingInitialData || !!this.census.isLoadingEmployees;
  }
  get hasMembersConnectionError(): boolean { return !!this.membersConnectionError; }
  get hasMembersStep(): boolean { return !this.data.snapshotMembers && !this.isManualMode && !this.isCreateFromSelectionFlow; }
  get canUseFilterDrivenMembersStep(): boolean {
    return !this.isMemberFallbackMode && this.census.canUseFilterControls();
  }
  get canUsePaycorFilterAutoAdd(): boolean {
    return this.census.usesLocalCensusFilters()
      && this.supportsProviderCapability('supportsAutoAddFromSavedRules');
  }
  get canShowAutoAddNewMembersControl(): boolean {
    if (this.census.usesLocalCensusFilters()) {
      return this.canUsePaycorFilterAutoAdd;
    }
    return this.canUseFilterDrivenMembersStep || this.canUsePaycorFilterAutoAdd;
  }
  get canShowManualMemberManagement(): boolean {
    return this.isMemberFallbackMode && !!this.data.team?.id && this.canCreateManualMembers;
  }

  get usesWallitInternalProviderMemberUpdates(): boolean {
    return this.isEdit
      && !this.isMemberFallbackMode
      && this.supportsProviderCapability('supportsInternalTeamCreationFromCensus')
      && !this.supportsProviderCapability('supportsFilteredTeams');
  }

  get fallbackBannerMessage(): string {
    if (!this.isMemberFallbackMode) {
      return '';
    }

    if (!this.canCreateManualMembers) {
      return 'Manual add-by-email and spreadsheet upload are not enabled for your account yet. Connect a payroll provider with filter support to use census filters and auto-add.';
    }

    return '';
  }

  get selectedMemberCount(): number {
    if (this.data.snapshotEditor) return this.data.snapshotEditor.counts?.selected || 0;
    if (this.data.snapshotBrowse) { return this.data.snapshotBrowse.selection.size; }
    if (this.data.snapshotSelectedEmployees) { return this.data.snapshotSelectedEmployees.size; }
    return this.data.snapshotSubmit ? (this.data.snapshotSelectionCount ?? 0) : this.selected.size;
  }

  get primarySaveLabel(): string {
    if (this.data.snapshotEditor) return this.isEdit ? 'Save team' : 'Create team';
    if (this.data.snapshotMembers) { return 'Save changes'; }
    if (this.snapshotMatching) { return 'Create team'; }
    if (this.data.snapshotSubmit) { return `Create team from ${this.selectedMemberCount} employees`; }
    if (this.isCreateFromSelectionFlow) {
      return `Create team with ${this.selectedMemberCount} members`;
    }
    return this.isEdit ? 'Save changes' : 'Create team';
  }

  get providerDisplayName(): string {
    const explicitName = `${this.data.providerDisplayName || ''}`.trim();
    if (explicitName) {
      return explicitName;
    }

    const providerName = `${this.data.providerConnection?.provider?.name || ''}`.trim();
    if (providerName) {
      return providerName;
    }

    const connectionName = `${this.data.providerConnection?.connection?.displayName || ''}`.trim();
    return connectionName || 'payroll provider';
  }

  get saveButtonLabel(): string {
    if (this.isSavingTeam) {
      if (this.data.snapshotEditor) return 'Saving team…';
      if (this.data.snapshotMembers) { return 'Saving team changes...'; }
      if (this.data.snapshotSubmit) { return 'Starting team creation...'; }
      if (this.isEdit) {
        return 'Saving team...';
      }
      return `Creating team with ${this.selectedMemberCount} members...`;
    }

    if (this.data.snapshotSubmit) { return this.primarySaveLabel; }
    if (this.hasMembersStep) {
      return `Save team with ${this.selected.size} members`;
    }

    return this.primarySaveLabel;
  }

  private get isDetailsStepReady(): boolean {
    return this.form.valid;
  }

  constructor(
    private payrollService: PayrollService,
    private spinnerService: SpinnerService,
    private usersService: UsersService,
    private payrollCensusService: PayrollCensusService,
    private teamCompaniesService: TeamCompaniesService,
    private teamsService: TeamsService,
    private payrollAccessService: PayrollAccessService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private ref: MatDialogRef<EditTeamDialogComponent, EditTeamDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: EditTeamDialogData
  ) {
    super();
    this.ref.disableClose = false;
    this.isEdit = !!data.isEdit;
    this.isManualMode = !!data.manualMode;
    this.isMemberFallbackMode = !!data.memberFallbackMode;
    this.isCreateFromSelectionFlow = !this.isEdit && !!data.skipMemberSelectionStep;

    this.census = this.payrollCensusService.createSession();
    const steps = data.snapshotMembers ? [{id: 'members', label: 'Edit members'}] : (!this.isManualMode && this.isCreateFromSelectionFlow)
      ? [{ id: 'details', label: 'Team details' }]
      : (!this.isManualMode
        ? [
        { id: 'details', label: 'Team details' },
        { id: 'members', label: 'Select members' },
      ]
        : [{ id: 'details', label: 'Team details' }]);
    this.initSteps(steps, 'sequential', 0);
    this.form.patchValue({
      teamName: data.team.name,
      wellnessAmount: data.team.amount,
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.data.snapshotEditor) {
      this.currentCompany = this.data.company;
      await this.retrySnapshotEditor();
      if (this.destroyed) return;
      if (this.data.startAtMembersStep) { this.markStepComplete(0); this.goToStep(1); }
      return;
    }
    this.currentCompany = this.data?.company;
    this.autoAddNewMembers = !!(
      this.data?.team?.autoAddNewMembers ||
      this.data?.team?.metadata?.autoAddNewMembers
    );

    if (this.data.snapshotBrowse) {
      // This dialog owns its page/selection lifetime; never initialize live census.
      await this.data.snapshotBrowse.setVisible(true);
      if (this.destroyed) { return; }
      await this.data.snapshotBrowse.open(this.currentCompany.id);
      if (!this.destroyed) {
        await this.data.snapshotMembers?.loadMembers();
        if (this.destroyed) { return; }
        await this.data.snapshotCriteria?.open(this.currentCompany.id, this.data.snapshotBrowse.context?.snapshot?.id || '', this.data.snapshotMembers?.teamId || '');
      }
      return;
    }
    if (this.isManualMode || this.data.snapshotSubmit) {
      return;
    }

    const me = this.usersService.me();
    if (!me) {
      return;
    }

    const payrollConfig = this.getPayrollConfig(this.currentCompany);
    if (payrollConfig?.teamsMap) {
      this.teamsMap = payrollConfig.teamsMap;
    }

    if (this.isMemberFallbackMode) {
      const payrollAccess = await this.payrollAccessService.refresh().catch(() => ({
        canCreatePayrollAdminManualMembers: false,
      } as any));
      this.canCreateManualMembers = payrollAccess.canCreatePayrollAdminManualMembers;
      await this.loadFallbackMembers();
    } else {
      try {
        await this.census.init({
          company: this.currentCompany,
          providerConnection: this.data.providerConnection,
          criteriaSnapshot: this.data.criteriaSnapshot,
          teamId: this.data.team?.id,
          teamsMap: this.teamsMap,
          selectedEmployeeIds: this.data.selectedEmployeeIds,
        });
        this.fallbackBaselineMembers = Array.isArray(this.data.selectedEmployeeIds)
          ? [...this.data.selectedEmployeeIds]
          : [];
        this.clearMembersConnectionError();
        this.syncAutoAddNewMembersState();
      } catch (error: any) {
        this.setMembersConnectionError(error);
      }
    }

    if (this.hasMembersStep && this.data.startAtMembersStep) {
      this.markStepComplete(0);
      this.goToStep(1);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.snapshotError = ''; this.snapshotAffectedKeys = [];
    this.data.snapshotEditor?.destroy();
    this.data.snapshotBrowse?.destroy();
    this.data.snapshotCriteria?.destroy();
  }

  async retrySnapshotEditor(): Promise<void> {
    const editor = this.data.snapshotEditor;
    if (!editor || this.destroyed || this.isSavingTeam) return;
    await editor.open();
    if (this.destroyed || this.snapshotSettingsInitialized || !editor.baseline) return;
    // Initialize once, including after a failed first read. Later retries retain
    // both the user's draft and the original optimistic-concurrency baseline.
    this.form.patchValue({teamName: editor.baseline.name, wellnessAmount: editor.baseline.amount});
    this.snapshotSettingsInitialized = true;
  }

  get snapshotDetailsReadonly(): boolean {
    return this.isSavingTeam || !!this.data.snapshotProgress?.progress
      || !!this.data.snapshotEditor?.teamId && !this.snapshotSettingsInitialized;
  }

  private snapshotSelection(): Map<string, {employeeKey: string; name: string}> | undefined {
    return this.data.snapshotBrowse?.selection || this.data.snapshotSelectedEmployees;
  }

  private snapshotFence(): string {
    const state = this.data.snapshotBrowse;
    return JSON.stringify([this.data.company?.id, state?.companyId, state?.mode, state?.context?.snapshot?.id,
      state?.selectionSnapshotId, state?.selectionRevision, [...(this.snapshotSelection()?.keys() || [])].sort(),
      this.data.snapshotEditor?.intent(this.form.value.teamName || "", Number(this.form.value.wellnessAmount || 0)), this.data.snapshotMembers?.intent(), this.snapshotMatching, this.data.snapshotCriteria?.criteria, this.form.value]);
  }

  get snapshotFeedback(): string {
    // Once admitted, the details-only progress panel owns current failure/status
    // text. A captured submission error would become stale after Resume.
    if (this.data.snapshotSubmit && !this.data.snapshotMembers && !this.data.snapshotBrowse && !this.data.snapshotEditor
      && this.data.snapshotProgress?.progress) return '';
    return !this.destroyed && this.data.snapshotIsCurrent?.() !== false && this.snapshotErrorFence === this.snapshotFence()
      ? this.snapshotError : '';
  }

  get snapshotAffectedEmployees(): Array<{employeeKey: string; name: string}> {
    if (!this.snapshotFeedback) { return []; }
    const selected = this.snapshotSelection();
    return this.snapshotAffectedKeys.reduce((employees, key) => {
      const employee = selected?.get(key);
      if (employee) { employees.push(employee); }
      return employees;
    }, [] as Array<{employeeKey: string; name: string}>);
  }

  deselectSnapshotAffected(): void {
    if (this.isSavingTeam || !this.snapshotFeedback) { return; }
    const selected = this.snapshotSelection();
    // This map contains only explicit additions. Existing memberships/removals are separate.
    this.snapshotAffectedEmployees.forEach(employee => selected?.delete(employee.employeeKey));
    if (this.data.snapshotBrowse) { ++this.data.snapshotBrowse.selectionRevision; }
    this.snapshotError = ''; this.snapshotAffectedKeys = [];
  }

  onSnapshotSelectionModeChanged(): void {
    this.snapshotError = ''; this.snapshotAffectedKeys = [];
    const criteria = this.data.snapshotCriteria;
    const snapshotId = this.data.snapshotBrowse?.context?.snapshot?.id;
    if (criteria && snapshotId && this.snapshotMatching) {
      if (criteria.snapshotId !== snapshotId) { void criteria.open(this.currentCompany.id, snapshotId); }
      if (!criteria.criteria.allEligible) { void criteria.loadOptions(); }
    }
  }

  get snapshotSaveDisabled(): boolean {
    // A details-only selection draft is consumed by its admitted operation.
    // Recovery uses that operation; completing it must not re-enable a new Save.
    if (this.data.snapshotSubmit && !this.data.snapshotMembers && !this.data.snapshotBrowse && !this.data.snapshotEditor
      && this.data.snapshotProgress?.progress) return true;
    if (this.data.snapshotEditor) return !this.data.snapshotEditor.ready || this.form.invalid
      || this.isEdit && !this.snapshotSettingsInitialized
      || !this.isEdit && (!this.data.snapshotEditor.counts?.selected || this.data.snapshotEditor.counting)
      || !!this.data.snapshotProgress?.progress && !['complete', 'closed'].includes(this.data.snapshotProgress.progress.phase);
    if (this.data.snapshotIsCurrent?.() === false) { return true; }
    if (this.data.snapshotMembers) { return !this.data.snapshotMembers.canSave; }
    if (this.data.snapshotSelectedEmployees && !this.data.snapshotSelectedEmployees.size) { return true; }
    const state = this.data.snapshotBrowse;
    if (this.snapshotMatching) {
      const criteria = this.data.snapshotCriteria;
      return !criteria?.valid || !criteria.ready || criteria.loading || criteria.busy || state?.mode !== 'snapshot' || state.loading
        || state.context?.availability !== 'ready' || criteria.snapshotId !== state.context?.snapshot?.id;
    }
    return !!state && (state.mode !== 'snapshot' || state.loading || !state.selection.size
      || state.selectionSnapshotId !== state.context?.snapshot?.id);
  }

  override getStepState(index: number): StepState {
    if (!this.hasMembersStep) {
      return super.getStepState(index);
    }

    if (index === 0 && this.isDetailsStepReady) {
      return 'complete';
    }

    if (index === 1 && this.isDetailsStepReady) {
      return 'available';
    }

    return super.getStepState(index);
  }

  override canNavigateToStep(index: number): boolean {
    if (this.isSavingTeam) {
      return false;
    }

    if (this.hasMembersStep && index === 1) {
      return this.isDetailsStepReady;
    }

    return super.canNavigateToStep(index);
  }

  // ----- Step navigation -----
  close(): void {
    if (this.isSavingTeam) {
      return;
    }
    this.ngOnDestroy();
    this.ref.close({ saved: false });
  }

  async nextFromDetails(): Promise<void> {
    if (this.isSavingTeam) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.hasMembersStep) {
      await this.save();
      return;
    }

    // Unlock the next step in sequential mode.
    this.markStepComplete(0);
    this.goToStep(1);
  }

  backToDetails(): void {
    if (this.isSavingTeam) {
      return;
    }
    this.goToStep(0);
  }

  onWellnessAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input?.value;
    if (rawValue === '' || rawValue == null) {
      return;
    }

    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      return;
    }

    if (parsed > this.wellnessAmountMax) {
      const clamped = this.wellnessAmountMax.toFixed(2);
      input.value = clamped;
      this.form.controls.wellnessAmount.setValue(Number(clamped), { emitEvent: false });
    }
  }

  // ---- Helpers ----
  get filteredEmployees(): any[] {
    return this.census.getFilteredEmployees(this.search);
  }

  get allVisibleSelected(): boolean {
    const visibleEmployeeKeys = this.getVisibleEmployeeKeys();
    if (!visibleEmployeeKeys.length) {
      return false;
    }
    return visibleEmployeeKeys.every((employeeKey) => !!this.selectedEmails[employeeKey]);
  }

  get someVisibleSelected(): boolean {
    const visibleEmployeeKeys = this.getVisibleEmployeeKeys();
    if (!visibleEmployeeKeys.length) {
      return false;
    }
    const anySelected = visibleEmployeeKeys.some((employeeKey) => !!this.selectedEmails[employeeKey]);
    const allSelected = visibleEmployeeKeys.every((employeeKey) => !!this.selectedEmails[employeeKey]);
    return anySelected && !allSelected;
  }

  activeFilterCount(filterKey: string): number {
    const values = this.activeFiltersMap?.[filterKey];
    return Array.isArray(values) ? values.length : 0;
  }

  employeeFilterSummary(e: any): string {
    return this.employeeFilterMetadata(e)
      .map((metadata) => metadata.displayValue)
      .join(' • ');
  }

  displayFilterValue(filterKey: string, value: string): string {
    const rawValue = `${value ?? ''}`.trim();
    if (!rawValue) {
      return '';
    }

    const normalizedFilterKey = this.normalizeFilterKey(filterKey);
    if (!['location', 'department', 'division'].includes(normalizedFilterKey)) {
      return rawValue;
    }

    const escapedFilterKey = `${filterKey || ''}`.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escapedFilterKey) {
      return rawValue;
    }

    return rawValue.replace(new RegExp(`^${escapedFilterKey}\\s*\\/\\s*`, 'i'), '').trim();
  }

  isEmployeeSelected(employee: any): boolean {
    const employeeKey = this.employeeKey(employee);
    return !!this.selectedEmails[employeeKey] || this.selected.has(employeeKey);
  }

  private employeeFilterMetadata(employee: any): EmployeeFilterMetadata[] {
    const metadata: EmployeeFilterMetadata[] = [];
    const seenFilterKeys = new Set<string>();

    (this.availableFilters || []).forEach((filter) => {
      const rawFilterKey = `${filter?.[0] || ''}`.trim();
      if (!rawFilterKey) {
        return;
      }

      const normalizedFilterKey = this.normalizeFilterKey(rawFilterKey);
      if (!normalizedFilterKey || seenFilterKeys.has(normalizedFilterKey)) {
        return;
      }
      seenFilterKeys.add(normalizedFilterKey);

      const value = this.employeeFilterValue(employee, rawFilterKey, normalizedFilterKey);
      if (!value) {
        return;
      }

      metadata.push({
        label: rawFilterKey,
        value,
        displayValue: this.displayFilterValue(rawFilterKey, value),
      });
    });

    return metadata;
  }

  private employeeFilterValue(employee: any, rawFilterKey: string, normalizedFilterKey: string): string {
    const commonFilterValueKeys: Record<string, string[]> = {
      location: ['location', 'Default Location Full Path', 'Location'],
      division: ['division', 'Default Division Full Path', 'Division'],
      department: ['department', 'Default Department Full Path', 'Department', 'dept'],
      state: ['state', 'State'],
      paytype: ['payType', 'Pay Type'],
      employeetype: ['employeeType', 'Employee Type', 'employmentType', 'Employment Type', 'employment_type'],
      employmenttype: ['employmentType', 'Employment Type', 'employeeType', 'Employee Type', 'employment_type'],
      status: ['EmployeeStatus', 'Employee Status', 'EmploymentStatus', 'Employment Status', 'SourceStatus', 'Source Status', 'employeeStatus', 'employmentStatus', 'sourceStatus', 'status'],
      employeestatus: ['EmployeeStatus', 'Employee Status', 'EmploymentStatus', 'Employment Status', 'SourceStatus', 'Source Status', 'employeeStatus', 'employmentStatus', 'sourceStatus', 'status'],
      employmentstatus: ['EmploymentStatus', 'Employment Status', 'EmployeeStatus', 'Employee Status', 'SourceStatus', 'Source Status', 'employmentStatus', 'employeeStatus', 'sourceStatus', 'status'],
      sourcestatus: ['SourceStatus', 'Source Status', 'EmployeeStatus', 'Employee Status', 'EmploymentStatus', 'Employment Status', 'sourceStatus', 'employeeStatus', 'employmentStatus', 'status'],
    };

    const titleCaseKey = rawFilterKey
      .split(' ')
      .map((part) => part ? `${part[0].toUpperCase()}${part.slice(1)}` : '')
      .join(' ');
    const camelCaseKey = rawFilterKey
      .split(' ')
      .map((part, idx) => {
        if (!part) {
          return '';
        }
        const lower = part.toLowerCase();
        return idx === 0 ? lower : `${lower[0].toUpperCase()}${lower.slice(1)}`;
      })
      .join('');
    const snakeCaseKey = rawFilterKey
      .split(' ')
      .filter(Boolean)
      .map((part) => part.toLowerCase())
      .join('_');

    const candidates = [
      ...(commonFilterValueKeys[normalizedFilterKey] || []),
      rawFilterKey,
      titleCaseKey,
      camelCaseKey,
      snakeCaseKey,
      `Default ${titleCaseKey} Full Path`,
    ];

    for (const key of candidates) {
      const value = `${employee?.[key] ?? ''}`.trim();
      if (value) {
        return value;
      }
    }

    return '';
  }

  private normalizeFilterKey(filterKey: string): string {
    return `${filterKey || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private getVisibleEmployeeKeys(): string[] {
    return (this.filteredEmployees || [])
      .filter((employee) => this.hasEmployeeEmail(employee))
      .map((employee) => this.employeeKey(employee))
      .filter((employeeKey) => !!employeeKey);
  }

  // ---- Step 2 actions forwarded to session ----
  async toggleEmployee(email: string, checked: boolean): Promise<void> {
    if (!checked) {
      const confirmed = await this.confirmAutoAddDeactivation([email]);
      if (!confirmed) {
        return;
      }
    }

    this.census.toggleEmployee(email, checked);
    this.syncAutoAddNewMembersState();
  }

  selectAllVisible(): void {
    this.getVisibleEmployeeKeys().forEach((employeeKey) => {
      this.census.selected.add(employeeKey);
      this.census.selectedEmails[employeeKey] = true;
    });
    this.syncAutoAddNewMembersState();
  }

  async toggleAllVisible(checked: boolean): Promise<void> {
    if (checked) {
      this.selectAllVisible();
      return;
    }
    await this.deselectAllVisible();
  }

  async deselectAllVisible(): Promise<void> {
    const visibleSelectedEmployeeKeys = this.getVisibleEmployeeKeys()
      .filter((employeeKey) => this.selected.has(employeeKey) || !!this.selectedEmails[employeeKey]);

    const confirmed = await this.confirmAutoAddDeactivation(visibleSelectedEmployeeKeys);
    if (!confirmed) {
      return;
    }

    visibleSelectedEmployeeKeys.forEach((employeeKey) => {
      this.census.selected.delete(employeeKey);
      delete this.census.selectedEmails[employeeKey];
    });
    this.syncAutoAddNewMembersState();
  }

  async handleFilterSelect(filterKey: string, filterValue: string | string[]): Promise<void> {
    try {
      await this.census.handleFilterSelect(filterKey, filterValue);
      this.clearMembersConnectionError();
    } catch (error: any) {
      this.setMembersConnectionError(error);
    } finally {
      this.syncAutoAddNewMembersState();
    }
  }

  async clearFilter(key: string, value: string): Promise<void> {
    try {
      await this.census.clearFilter(key, value);
      this.clearMembersConnectionError();
    } catch (error: any) {
      this.setMembersConnectionError(error);
    } finally {
      this.syncAutoAddNewMembersState();
    }
  }

  async clearAllFilters(): Promise<void> {
    try {
      await this.census.clearAllFilters();
      this.clearMembersConnectionError();
    } catch (error: any) {
      this.setMembersConnectionError(error);
    } finally {
      this.syncAutoAddNewMembersState();
    }
  }

  get canEnableAutoAddNewMembers(): boolean {
    if (this.isMemberFallbackMode || !this.canUseFilterDrivenMembersStep) {
      return false;
    }
    if (this.census.usesLocalCensusFilters() && !this.canUsePaycorFilterAutoAdd) {
      return false;
    }
    return this.census.isAutoAddEligible();
  }

  get autoAddNewMembersDisabledReason(): string {
    if (this.isMemberFallbackMode) {
      return 'Auto-add requires a healthy payroll provider connection and census filters.';
    }
    return this.census.getAutoAddEligibilityMessage();
  }

  get autoAddNewMembersTooltip(): string {
    if (!this.canEnableAutoAddNewMembers) {
      return this.autoAddNewMembersDisabledReason;
    }
    return 'When enabled, any new team member added in your payroll system who meets the defined filters will be automatically added to this team.';
  }

  private syncAutoAddNewMembersState(): void {
    if (!this.canEnableAutoAddNewMembers) {
      this.autoAddNewMembers = false;
    }
  }

  private async confirmAutoAddDeactivation(employeeKeysToRemove: string[]): Promise<boolean> {
    if (!this.wouldDeactivateAutoAdd(employeeKeysToRemove)) {
      return true;
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Turn Off Auto-add?',
        'Removing this employee from the team will turn off Auto-add new Team Members because the team will no longer include every employee matching the current filters.<br><br>Cancel to keep Auto-add on, or make the update to continue.',
        false,
        'Update',
        'Cancel',
      ),
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    return !!confirmed;
  }

  private async confirmPendingSettlementDeactivation(memberCount: number): Promise<boolean> {
    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Deactivate Members?',
        `${memberCount} member${memberCount === 1 ? '' : 's'} cannot be fully removed until pending ACH activity settles. Deactivate now so they stop receiving new rewards, allowances, and reimbursements. Final removal will complete after settlement.`,
        false,
        'Deactivate now',
        'Cancel',
      ),
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    return !!confirmed;
  }

  private wouldDeactivateAutoAdd(employeeKeysToRemove: string[]): boolean {
    if (!this.autoAddNewMembers || !employeeKeysToRemove.length) {
      return false;
    }

    const nextSelectedKeys = new Set(
      Array.from(this.selected || [])
        .map((employeeKey) => this.normalizeEmployeeKey(employeeKey))
        .filter((employeeKey) => !!employeeKey)
    );

    employeeKeysToRemove
      .map((employeeKey) => this.normalizeEmployeeKey(employeeKey))
      .filter((employeeKey) => !!employeeKey)
      .forEach((employeeKey) => nextSelectedKeys.delete(employeeKey));

    const activeAvailableEmployeeKeys = new Set(
      (this.census.getFilterMatchedEmployees() || [])
        .filter((employee) => this.isActiveEmployee(employee))
        .map((employee) => this.normalizeEmployeeKey(this.employeeKey(employee)))
        .filter((employeeKey) => !!employeeKey)
    );

    if (!activeAvailableEmployeeKeys.size) {
      return false;
    }

    for (const employeeKey of activeAvailableEmployeeKeys) {
      if (!nextSelectedKeys.has(employeeKey)) {
        return true;
      }
    }

    return false;
  }

  private isActiveEmployee(employee: any): boolean {
    const status = `${employee?.['Employee Status']
      ?? employee?.EmployeeStatus
      ?? employee?.['Employment Status']
      ?? employee?.EmploymentStatus
      ?? employee?.['Source Status']
      ?? employee?.SourceStatus
      ?? employee?.employeeStatus
      ?? employee?.employmentStatus
      ?? employee?.sourceStatus
      ?? employee?.status
      ?? ''}`.trim().toLowerCase();
    return status === '' || status === 'active';
  }

  private normalizeEmployeeKey(employeeKey: string | undefined | null): string {
    return `${employeeKey || ''}`.trim().toLowerCase();
  }

  private getCurrentBrand(): string {
    const brand = this.usersService.me()?.brand || {};
    return this.normalizeBrandValue(
      brand.redirectUrl
      || brand.shortName
      || brand.brand
      || brand.name
      || brand.partnerId
      || ''
    );
  }

  private normalizeBrandValue(value: any): string {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    const withoutProtocol = normalized.replace(/^https?:\/\//, '');
    const shortName = withoutProtocol.includes('.') ? withoutProtocol.split('.')[0] : withoutProtocol;
    return shortName.replace(/[^a-z0-9_-]/g, '');
  }

  async onEditorScroll(event: Event): Promise<void> {
    const editor = this.data.snapshotEditor, viewport = event.target as HTMLElement;
    if (!editor || editor.loading || this.isSavingTeam) return;
    const bottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 80;
    const top = viewport.scrollTop < 40;
    if (!bottom && !top) return;
    const viewportTop = viewport.getBoundingClientRect().top;
    const anchor = Array.from(viewport.querySelectorAll<HTMLElement>('[data-employee]')).find(row => row.getBoundingClientRect().bottom >= viewportTop);
    const key = anchor?.dataset['employee'], offset = anchor ? anchor.getBoundingClientRect().top - viewportTop : 0;
    await editor.more(!bottom && top ? editor.rows[0]?.rowId : undefined);
    setTimeout(() => {
      if (this.destroyed || !key) return;
      const row = Array.from(viewport.querySelectorAll<HTMLElement>('[data-employee]')).find(item => item.dataset['employee'] === key);
      if (row) viewport.scrollTop += row.getBoundingClientRect().top - viewport.getBoundingClientRect().top - offset;
    });
  }

  // ---- Save delegates to session.saveTeam ----
  async save(): Promise<void> {
    if (this.isSavingTeam) {
      return;
    }

    // Membership-only edits do not validate or change hidden team settings.
    if (!this.data.snapshotMembers && this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.data.snapshotSubmit) {
      if (this.data.snapshotMembers && !this.data.snapshotMembers.hasChanges) { this.close(); return; }
      if (this.snapshotSaveDisabled) { return; }
      this.isSavingTeam = true;
      this.snapshotError = ''; this.snapshotAffectedKeys = [];
      const fence = this.snapshotFence();
      this.ref.disableClose = true;
      try {
        let matching: MatchingSelection | undefined;
        if (this.snapshotMatching && !this.data.snapshotMembers && this.data.snapshotCriteria) {
          await this.data.snapshotCriteria.loadPreview();
          if (this.destroyed || this.data.snapshotIsCurrent?.() === false || fence !== this.snapshotFence()) { return; }
          const selection = this.data.snapshotCriteria.selection;
          if (!selection) { throw new Error(this.data.snapshotCriteria.error || 'Unable to check matching employees. Try again.'); }
          if (!selection.eligibleRows) { throw new Error('No eligible employees match these filters. Adjust the filters before creating this team.'); }
          matching = selection;
        }
        await this.data.snapshotSubmit(this.form.value.teamName?.trim() || '', Number(this.form.value.wellnessAmount || 0), matching);
        if (!this.destroyed && this.data.snapshotIsCurrent?.() !== false) { this.ref.close({saved: false}); }
      } catch (error: any) {
        if (this.destroyed || this.data.snapshotIsCurrent?.() === false || fence !== this.snapshotFence()) { return; }
        this.snapshotErrorFence = fence;
        const keys = error?.employeeKeys;
        this.snapshotAffectedKeys = error?.submissionRejected && Array.isArray(keys) && keys.length <= 100
          && keys.every(key => typeof key === 'string' && this.snapshotSelection()?.has(key)) ? [...new Set<string>(keys)] : [];
        this.snapshotError = error?.message || 'Submission could not be confirmed. Retry with the same details or reload to Resume.';
        if (!error?.submissionRejected) { this.snackBar.openFromComponent(SnackBarComponent, {data: this.snapshotError}); }
      } finally { this.isSavingTeam = false; this.ref.disableClose = false; }
      return;
    }
    if (this.isManualMode) {
      await this.saveManualTeam();
      return;
    }
    if (this.isMemberFallbackMode) {
      await this.saveFallbackTeamEdits();
      return;
    }
    if (this.usesWallitInternalProviderMemberUpdates) {
      await this.saveWallitInternalTeamEdits();
      return;
    }
    if (this.hasMembersConnectionError && this.hasMembersStep) {
      return;
    }
    if (!this.selected.size) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Select at least one team member before saving.',
      });
      return;
    }

    const teamName = this.form.value.teamName?.trim?.() || '';
    const wellnessAmount = Number(this.form.value.wellnessAmount ?? 0);

    const brand = this.getCurrentBrand();
    const activeCriteriaSnapshot = this.census.getActiveCriteriaSnapshot();
    const shouldCreatePaycorSavedFilterTeam =
      !this.isEdit
      && !!activeCriteriaSnapshot
      && this.census.areAllAvailableEmployeesSelected();
    const fieldsContainForSave = this.isCreateFromSelectionFlow
      ? this.cloneFieldsContain(this.data.initialFieldsContain || {})
      : this.census.getFieldsContainForRequestSnapshot();

    let saveResult: any;
    const effectiveAutoAddNewMembers = this.autoAddNewMembers && this.canEnableAutoAddNewMembers;
    this.beginTeamSave();
    try {
      saveResult = await this.census.saveTeam({
        brand,
        companyId: this.currentCompany.id,
        isEdit: this.isEdit,
        teamId: this.data.team.id,
        teamName,
        wellnessAmount,
        autoAddNewMembers: effectiveAutoAddNewMembers,
        fieldsContain: fieldsContainForSave,
        criteriaSnapshot: shouldCreatePaycorSavedFilterTeam ? activeCriteriaSnapshot : null,
      });
    } catch (error: any) {
      const message =
        error?.error?.error ||
        error?.error?.message ||
        error?.error ||
        error?.message ||
        'Unable to save team right now.';
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: message,
      });
      this.endTeamSave();
      return;
    }

    const savedTeamId =
      this.getSavedTeamId(saveResult) ||
      this.getSavedTeamId(this.data.team) ||
      this.data.team.id ||
      '';

    if (!shouldCreatePaycorSavedFilterTeam) {
      await this.persistTeamFilters(savedTeamId, fieldsContainForSave).catch(() => undefined);
    }

    this.ref.close({
      saved: true,
      teamId: savedTeamId,
      teamName,
      wellnessAmount,
      selectedEmployeeIds: Array.from(this.selected),
      autoAddNewMembers: effectiveAutoAddNewMembers,
    });
  }

  private getSavedTeamId(source: any): string {
    return source?.id || source?.teamId || source?.team?.id || '';
  }

  private async loadFallbackMembers(): Promise<void> {
    try {
      const employees = await this.teamsService.getPayrollUsersForCompany(this.currentCompany.id);
      this.fallbackBaselineMembers = Array.isArray(this.data.selectedEmployeeIds)
        ? [...this.data.selectedEmployeeIds]
        : [];
      this.census.availableEmployees = Array.isArray(employees) ? employees : [];
      this.census.availableFilters = [];
      this.census.originalAvailableFilters = {};
      this.census.originalFilterKeys = {};
      this.census.activeFilters = [];
      this.census.activeFiltersMap = {};
      this.census.selected = new Set<string>();
      this.census.selectedEmails = {};
      for (const employee of this.data.selectedEmployeeIds || []) {
        const employeeKey = this.employeeKey(employee);
        if (!employeeKey) {
          continue;
        }
        this.census.selected.add(employeeKey);
        this.census.selectedEmails[employeeKey] = true;
      }
      this.census.isLoadingInitialData = false;
      this.clearMembersConnectionError();
      this.syncAutoAddNewMembersState();
    } catch (error: any) {
      this.setMembersConnectionError(error);
    }
  }

  employeeKey(employee: any): string {
    return employee?.['Primary Email'] || employee?.PrimaryEmail || employee?.primaryEmail || employee?.email || employee?.userEmail || employee?.['Employee Id'] || employee?.EmployeeId || employee?.employeeId || employee?.id || '';
  }

  employeeEmail(employee: any): string {
    return `${employee?.['Primary Email'] || employee?.PrimaryEmail || employee?.primaryEmail || employee?.email || employee?.userEmail || ''}`.trim();
  }

  hasEmployeeEmail(employee: any): boolean {
    return !!this.employeeEmail(employee);
  }

  missingEmployeeEmailTooltip(employee: any): string {
    if (this.hasEmployeeEmail(employee)) {
      return '';
    }
    if (this.isMemberFallbackMode) {
      return 'This employee does not have an email address, so they cannot be added to this Wallit team yet.';
    }
    return `Add an email address in ${this.providerDisplayName} before importing this employee to Wallit.`;
  }

  private async saveFallbackTeamEdits(): Promise<void> {
    return this.saveWallitInternalTeamEdits();
  }

  private async saveWallitInternalTeamEdits(): Promise<void> {
    const teamId = this.data.team.id || '';
    const teamName = this.form.value.teamName?.trim?.() || '';
    const wellnessAmount = Number(this.form.value.wellnessAmount ?? 0);
    if (!teamId || !teamName) {
      return;
    }
    if (!this.selected.size) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Select at least one team member before saving.',
      });
      return;
    }

    const initialMembers = Array.isArray(this.data.selectedEmployeeIds) ? this.data.selectedEmployeeIds : [];
    const baselineMembers = this.fallbackBaselineMembers.length ? this.fallbackBaselineMembers : initialMembers;
    const initialByKey = new Map<string, any>();
    for (const member of baselineMembers) {
      const key = this.employeeKey(member);
      if (key) {
        initialByKey.set(key, member);
      }
    }

    const availableByKey = new Map<string, any>();
    for (const employee of this.availableEmployees || []) {
      const key = this.employeeKey(employee);
      if (key) {
        availableByKey.set(key, employee);
      }
    }

    const selectedKeys = Array.from(this.selected);
    const keysToAdd = selectedKeys.filter((key) => !initialByKey.has(key));
    const keysToRemove = Array.from(initialByKey.keys()).filter((key) => !this.selected.has(key));
    const activeCriteriaSnapshot = this.census.getActiveCriteriaSnapshot();
    const effectiveAutoAddNewMembers = this.autoAddNewMembers && this.canEnableAutoAddNewMembers;
    const nextPaycorMetadata = this.buildPaycorSavedCriteriaTeamMetadata(
      activeCriteriaSnapshot,
      effectiveAutoAddNewMembers,
    );
    let shouldDeactivateRemovedMembers = false;
    if (keysToRemove.length) {
      const pendingCharge = await this.teamsService.checkTeamPendingCharges(teamId);
      shouldDeactivateRemovedMembers = !!pendingCharge?.hasPendingCharges;
      if (shouldDeactivateRemovedMembers) {
        const confirmedDeactivation = await this.confirmPendingSettlementDeactivation(keysToRemove.length);
        if (!confirmedDeactivation) {
          return;
        }
      }
    }

    this.beginTeamSave();
    try {
      await this.teamsService.modifyTeam(teamId, {
        name: teamName,
        amount: wellnessAmount,
        ...(nextPaycorMetadata ? { metadata: nextPaycorMetadata } : {}),
      });

      if (this.usesWallitInternalProviderMemberUpdates && keysToAdd.length) {
        const brand = this.getCurrentBrand();
        const employeeEmails = keysToAdd
          .map((key) => this.employeeEmail(availableByKey.get(key)))
          .filter((email) => !!email);

        if (employeeEmails.length) {
          await this.payrollService.addTeamMembersFromCensusEmployeeEmails(
            brand,
            this.currentCompany.id,
            teamId,
            { employeeEmails },
          );
        }
      } else {
        for (const key of keysToAdd) {
          const employee = availableByKey.get(key);
          const email = this.employeeEmail(employee);
          const userId = employee?.userId || '';
          if (!email) {
            continue;
          }
          await this.teamsService.addTeamMember(
            teamId,
            email,
            userId,
            undefined,
            { payrollCompanyMemberSelection: true },
          );
        }
      }

      for (const key of keysToRemove) {
        const member = initialByKey.get(key);
        const memberId = member?.memberId || member?.id || '';
        if (!memberId) {
          continue;
        }
        if (shouldDeactivateRemovedMembers) {
          await this.teamsService.deactivateTeamMemberPendingSettlement(memberId, teamId, false);
        } else {
          await this.teamsService.removeTeamMember(memberId, teamId, false);
        }
      }

      if (nextPaycorMetadata) {
        await this.persistPaycorSavedCriteria(teamId, activeCriteriaSnapshot, nextPaycorMetadata)
          .catch(() => undefined);
      }

      this.ref.close({
        saved: true,
        teamId,
        teamName,
        wellnessAmount,
        selectedEmployeeIds: selectedKeys,
        autoAddNewMembers: effectiveAutoAddNewMembers,
      });
    } catch (error) {
      this.endTeamSave();
      throw error;
    }
  }

  async onAddFallbackTeamMember(): Promise<void> {
    const teamId = this.data.team?.id || '';
    const email = `${this.manualMemberEmail || ''}`.trim();
    if (!this.canCreateManualMembers || !teamId || !email || this.isAddingManualMember) {
      return;
    }

    this.isAddingManualMember = true;
    try {
      await this.teamsService.addTeamMember(teamId, email);
      this.manualMemberEmail = '';
      await this.reloadFallbackMembers();
    } catch (error: any) {
      const errorMessage = error?.status === 409
        ? 'That user is already on this team.'
        : error?.error || error?.data?.error || 'Unable to add team member.';
      this.snackBar.openFromComponent(SnackBarComponent, { data: errorMessage });
    } finally {
      this.isAddingManualMember = false;
    }
  }

  async onUploadFallbackTeamMembers(data: any): Promise<void> {
    const teamId = this.data.team?.id || '';
    if (!this.canCreateManualMembers || !teamId || this.isUploadingManualMembers) {
      return;
    }

    this.isUploadingManualMembers = true;
    this.manualMemberUploadResult = null;
    try {
      this.manualMemberUploadResult = await this.teamsService.uploadMemberList(teamId, data);
      await this.reloadFallbackMembers();
    } catch (error: any) {
      this.manualMemberUploadResult = error?.error || {};
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: error?.error?.errorMessage || 'Unable to upload members right now.',
      });
    } finally {
      this.isUploadingManualMembers = false;
    }
  }

  private async reloadFallbackMembers(): Promise<void> {
    const teamId = this.data.team?.id || '';
    if (!teamId || !this.currentCompany?.id) {
      return;
    }

    const [teamMembers, employees] = await Promise.all([
      this.teamsService.getTeamMembersWithDailyLoginInfo(teamId),
      this.teamsService.getPayrollUsersForCompany(this.currentCompany.id),
    ]);

    this.fallbackBaselineMembers = Array.isArray(teamMembers) ? [...teamMembers] : [];
    this.data.selectedEmployeeIds = this.fallbackBaselineMembers;
    this.census.availableEmployees = Array.isArray(employees) ? employees : [];
    this.census.selected = new Set<string>();
    this.census.selectedEmails = {};

    for (const member of this.fallbackBaselineMembers) {
      const memberKey = this.employeeKey(member);
      if (!memberKey) {
        continue;
      }
      this.census.selected.add(memberKey);
      this.census.selectedEmails[memberKey] = true;
    }
  }

  private async saveManualTeam(): Promise<void> {
    const teamName = this.form.value.teamName?.trim?.() || '';
    const wellnessAmount = Number(this.form.value.wellnessAmount ?? 0);
    if (!teamName || !this.currentCompany?.id) {
      return;
    }

    this.beginTeamSave();
    try {
      let savedTeamId = this.data.team.id || '';
      if (this.isEdit && this.data.team.id) {
        await this.teamsService.modifyTeam(this.data.team.id, {
          name: teamName,
          amount: wellnessAmount,
        });
      } else {
        const result = await this.teamsService.createCompanyScopedPayrollTeam(this.currentCompany.id, {
          name: teamName,
          amount: wellnessAmount,
          metadata: this.data.team?.metadata || {},
        });
        savedTeamId = result?.id || '';
      }

      this.ref.close({
        saved: true,
        teamId: savedTeamId,
        teamName,
        wellnessAmount,
        selectedEmployeeIds: [],
        autoAddNewMembers: false,
      });
    } catch (error) {
      this.endTeamSave();
      throw error;
    }
  }

  private async persistTeamFilters(teamId: string, fieldsContain: Record<string, any>): Promise<void> {
    if (!teamId || !this.currentCompany?.id) {
      return;
    }

    const latestCompany = await this.teamCompaniesService.getCompany(this.currentCompany.id).catch(() => null);
    const company = latestCompany || this.currentCompany;
    const payrollConfig = this.getPayrollConfig(company);

    const nextTeamsMap = { ...(payrollConfig?.teamsMap || {}) };
    const existingTeamConfig = nextTeamsMap[teamId] || {};
    nextTeamsMap[teamId] = {
      ...existingTeamConfig,
      originalFilters: {
        ...(existingTeamConfig?.originalFilters || {}),
        fieldsContain: this.cloneFieldsContain(fieldsContain),
      },
    };

    const nextPayrollConfig = {
      ...payrollConfig,
      teamsMap: nextTeamsMap,
    };

    await this.teamCompaniesService.modifyCompany(this.currentCompany.id, {
      payrollConfig: nextPayrollConfig,
    });

    this.teamsMap = nextTeamsMap;
    this.currentCompany = {
      ...(this.currentCompany || {}),
      payrollConfig: nextPayrollConfig,
    };
    this.usersService.reloadMe();
  }

  private buildPaycorSavedCriteriaTeamMetadata(
    criteriaSnapshot: PayrollCensusAttributeSnapshotCriteria | null,
    autoAddNewMembers: boolean,
  ): Record<string, any> | null {
    if (!this.usesWallitInternalProviderMemberUpdates || !this.isPaycorProvider()) {
      return null;
    }

    const metadata: Record<string, any> = { ...(this.data.team?.metadata || {}) };
    metadata.membershipManagedBy = 'wallit';
    metadata.payrollProviderSlug = 'paycor';
    metadata.autoAddNewMembers = !!criteriaSnapshot && autoAddNewMembers;

    if (criteriaSnapshot) {
      metadata.payrollTeamSource = 'provider_census_attribute_snapshot';
      metadata.criteriaSnapshot = criteriaSnapshot;
      metadata.snapshotCreatedAt = metadata.snapshotCreatedAt || new Date().toISOString();
      delete metadata.snapshotCounts;
      return metadata;
    }

    metadata.payrollTeamSource = 'provider_census_selection';
    delete metadata.criteriaSnapshot;
    delete metadata.snapshotCreatedAt;
    delete metadata.snapshotCounts;
    return metadata;
  }

  private async persistPaycorSavedCriteria(
    teamId: string,
    criteriaSnapshot: PayrollCensusAttributeSnapshotCriteria | null,
    teamMetadata: Record<string, any>,
  ): Promise<void> {
    if (!teamId || !this.currentCompany?.id) {
      return;
    }

    const latestCompany = await this.teamCompaniesService.getCompany(this.currentCompany.id).catch(() => null);
    const company = latestCompany || this.currentCompany;
    const payrollConfig = this.getPayrollConfig(company);
    const nextTeamsMap = { ...(payrollConfig?.teamsMap || {}) };
    const existingTeamConfig = { ...(nextTeamsMap[teamId] || {}) };
    const nextTeamConfig = {
      ...existingTeamConfig,
      membershipManagedBy: 'wallit',
      payrollProviderSlug: 'paycor',
      payrollTeamSource: teamMetadata.payrollTeamSource,
      autoAddNewMembers: teamMetadata.autoAddNewMembers === true,
      originalFilters: {
        ...(existingTeamConfig?.originalFilters || {}),
        fieldsContain: this.cloneFieldsContain(this.census.getFieldsContainForRequestSnapshot()),
      },
    };

    if (criteriaSnapshot) {
      nextTeamConfig.criteriaSnapshot = criteriaSnapshot;
      nextTeamConfig.snapshotCreatedAt = teamMetadata.snapshotCreatedAt;
    } else {
      delete nextTeamConfig.criteriaSnapshot;
      delete nextTeamConfig.snapshotCreatedAt;
      delete nextTeamConfig.snapshotCounts;
    }

    nextTeamsMap[teamId] = nextTeamConfig;
    const nextPayrollConfig = {
      ...payrollConfig,
      teamsMap: nextTeamsMap,
    };

    await this.teamCompaniesService.modifyCompany(this.currentCompany.id, {
      payrollConfig: nextPayrollConfig,
    });

    this.teamsMap = nextTeamsMap;
    this.currentCompany = {
      ...(this.currentCompany || {}),
      payrollConfig: nextPayrollConfig,
    };
    this.usersService.reloadMe();
  }

  private cloneFieldsContain(fieldsContain: Record<string, any>): Record<string, any> {
    const snapshot: Record<string, any> = {};
    for (const [key, value] of Object.entries(fieldsContain || {})) {
      snapshot[key] = Array.isArray(value) ? [...value] : value;
    }
    return snapshot;
  }

  private getPayrollConfig(company: any): Record<string, any> {
    const rawPayrollConfig = company?.payrollConfig;
    if (!rawPayrollConfig) {
      return {};
    }

    if (typeof rawPayrollConfig === 'string') {
      try {
        return JSON.parse(rawPayrollConfig);
      } catch (_) {
        return {};
      }
    }

    return typeof rawPayrollConfig === 'object' ? rawPayrollConfig : {};
  }

  private supportsProviderCapability(capability: keyof PayrollProviderCapabilities): boolean {
    if (!this.data.providerConnection) {
      return true;
    }

    const capabilities = this.data.providerConnection.capabilities
      || this.data.providerConnection.provider?.capabilities
      || {};
    return capabilities[capability] === true;
  }

  private isPaycorProvider(): boolean {
    return `${this.data.providerConnection?.providerSlug || this.data.providerConnection?.connection?.providerSlug || ''}`
      .trim()
      .toLowerCase() === 'paycor';
  }

  private beginTeamSave(): void {
    this.isSavingTeam = true;
    this.ref.disableClose = true;
  }

  private endTeamSave(): void {
    this.isSavingTeam = false;
    this.ref.disableClose = false;
  }

  goToCompanyConnections(): void {
    this.ref.close({ saved: false });
    void this.router.navigate(['payrolladmin', 'company', 'connections']);
  }

  private clearMembersConnectionError(): void {
    this.membersConnectionError = '';
  }

  private setMembersConnectionError(error: any): void {
    this.membersConnectionError = this.buildPayrollConnectionErrorMessage(error);
    this.census.isLoadingInitialData = false;
  }

  private buildPayrollConnectionErrorMessage(error: any): string {
    const status = error?.status ?? error?.originalError?.status;
    const apiErrorMessage = this.extractPayrollErrorMessage(error);
    const rawMessage = `${apiErrorMessage || error?.message || ''}`.trim();
    const normalizedMessage = rawMessage.toLowerCase();

    if (
      status === 401 ||
      status === 403 ||
      status === 422 ||
      normalizedMessage.includes('authenticate with ukg') ||
      normalizedMessage.includes('unable to fetch data from ukg') ||
      normalizedMessage.includes('current configuration')
    ) {
      const providerName = normalizedMessage.includes('ukg') ? 'UKG' : this.providerDisplayName;
      return `Payroll connection is in an Error state. Go to Company Connections to update your ${providerName} credentials and try again.`;
    }

    if (status === 502 || normalizedMessage.includes('unable to reach ukg')) {
      const providerName = normalizedMessage.includes('ukg') ? 'UKG' : this.providerDisplayName;
      return `Unable to reach ${providerName} right now. Check Company Connections and try again.`;
    }

    return `Unable to load employee data from ${this.providerDisplayName}. Check Company Connections and try again.`;
  }

  private extractPayrollErrorMessage(error: any): string {
    const candidates = [
      error?.data?.error,
      error?.data?.message,
      error?.error?.error,
      error?.error?.message,
      error?.message,
    ];

    for (const candidate of candidates) {
      const parsed = this.parsePayrollErrorMessage(candidate);
      if (parsed) {
        return parsed;
      }
    }

    return '';
  }

  private parsePayrollErrorMessage(value: any): string {
    const rawValue = `${value ?? ''}`.trim();
    if (!rawValue) {
      return '';
    }

    if (!rawValue.startsWith('{') && !rawValue.startsWith('[')) {
      return rawValue;
    }

    try {
      const parsed = JSON.parse(rawValue);
      return `${parsed?.error || parsed?.message || rawValue}`.trim();
    } catch (_) {
      return rawValue;
    }
  }
}
